import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge,
  Table, Dropdown, InputGroup, Modal, ListGroup
} from 'react-bootstrap';
import { 
  Users, Plus, Search, Filter, Phone, Mail, MapPin, 
  CreditCard, TrendingUp, Eye, Edit, UserPlus, X, Check
} from 'lucide-react';
import customerService from '../../services/customerService';

/**
 * Composant principal de gestion des clients
 * Interface responsive avec gestion des crédits
 */
const CustomersList = ({ onCustomerUpdated }) => {
  // État principal
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  // Filtres et statistiques
  const [filters, setFilters] = useState({
    status: 'all', // all, vip, credit, regular
    creditStatus: 'all', // all, has_credit, no_credit
    search: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    vip: 0,
    withCredit: 0,
    totalCredit: 0
  });

  // Modals de gestion des clients
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Responsive
  const [viewMode, setViewMode] = useState('cards'); // cards, table
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Formulaire client (création et modification)
  const [customerForm, setCustomerForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    // Champs revendeur
    isReseller: false,
    resellerDiscount: 3000
  });
  const [isEditing, setIsEditing] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Effect pour responsive
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setViewMode(mobile ? 'cards' : 'table');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Chargement initial
  useEffect(() => {
    loadCustomers();
  }, []);

  // Filtrage en temps réel
  useEffect(() => {
    applyFilters();
  }, [customers, filters, searchTerm]);

  /**
   * Charger la liste des clients
   */
  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await customerService.getCustomers();

      if (response.success) {
        const formattedCustomers = response.data.map(customer => 
          customerService.formatCustomerForDisplay(customer)
        );
        setCustomers(formattedCustomers);
        calculateStats(formattedCustomers);
        console.log('✅ Clients chargés:', formattedCustomers.length);
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des clients:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculer les statistiques
   */
  const calculateStats = (customersList) => {
    const stats = {
      total: customersList.length,
      vip: customersList.filter(c => c.isVip).length,
      resellers: customersList.filter(c => c.isReseller).length,
      withCredit: customersList.filter(c => c.totalPurchases > 0).length,
      totalCredit: customersList.reduce((sum, c) => sum + (c.totalPurchases || 0), 0)
    };
    setStats(stats);
  };

  /**
   * Appliquer les filtres
   */
  const applyFilters = () => {
    let filtered = [...customers];

    // Recherche par nom ou téléphone
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(search) ||
        (customer.phone && customer.phone.includes(search))
      );
    }

    // Filtre par statut
    if (filters.status !== 'all') {
      if (filters.status === 'reseller') {
        filtered = filtered.filter(c => c.isReseller);
      } else if (filters.status === 'vip') {
        filtered = filtered.filter(c => c.isVip);
      } else if (filters.status === 'regular') {
        filtered = filtered.filter(c => !c.isVip && !c.isReseller);
      }
    }

    // Filtre par crédit
    if (filters.creditStatus !== 'all') {
      if (filters.creditStatus === 'has_credit') {
        filtered = filtered.filter(c => c.totalPurchases > 0);
      } else if (filters.creditStatus === 'no_credit') {
        filtered = filtered.filter(c => c.totalPurchases === 0);
      }
    }

    setFilteredCustomers(filtered);
  };

  /**
   * Ouvrir le modal de création
   */
  const handleOpenCreateModal = () => {
    setCustomerForm({ 
      name: '', 
      phone: '', 
      email: '', 
      address: '',
      isReseller: false,
      resellerDiscount: 3000
    });
    setIsEditing(false);
    setShowCreateModal(true);
  };

  /**
   * Ouvrir le modal de modification
   */
  const handleOpenEditModal = (customer) => {
    setCustomerForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      // Champs revendeur
      isReseller: customer.isReseller || false,
      resellerDiscount: customer.resellerDiscount || 3000
    });
    setSelectedCustomer(customer);
    setIsEditing(true);
    setShowCreateModal(true); // Réutiliser le même modal
  };

  /**
   * Créer ou modifier un client
   */
  const handleSaveCustomer = async () => {
    try {
      setProcessing(true);
      setError(null);

      // Validation
      const validation = customerService.validateCustomerData(customerForm);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      let response;
      if (isEditing && selectedCustomer) {
        // Modification
        console.log('✏️ Modification du client:', selectedCustomer.id);
        response = await customerService.updateCustomer(selectedCustomer.id, customerForm);
      } else {
        // Création
        console.log('➕ Création d\'un nouveau client');
        response = await customerService.createCustomer(customerForm);
      }

      if (response.success) {
        setShowCreateModal(false);
        setCustomerForm({ 
          name: '', 
          phone: '', 
          email: '', 
          address: '',
          isReseller: false,
          resellerDiscount: 3000
        });
        
        if (isEditing && selectedCustomer) {
          // Modification : mettre à jour localement et notifier le parent
          const updatedCustomers = customers.map(customer => 
            customer.id === selectedCustomer.id 
              ? customerService.formatCustomerForDisplay(response.data)
              : customer
          );
          setCustomers(updatedCustomers);
          calculateStats(updatedCustomers);
          
          // Notifier le parent si callback fourni
          if (onCustomerUpdated) {
            onCustomerUpdated(response.data);
          }
          
          console.log('✅ Client modifié et liste mise à jour localement');
        } else {
          // Création : recharger complètement
          await reloadCustomers();
          console.log('✅ Nouveau client créé et liste rechargée');
        }
        
        console.log(`✅ Client ${isEditing ? 'modifié' : 'créé'} avec succès`);
      } else {
        setError(response.message || `Erreur lors de ${isEditing ? 'la modification' : 'la création'}`);
      }
    } catch (error) {
      console.error(`❌ Erreur lors de ${isEditing ? 'la modification' : 'la création'}:`, error);
      setError('Erreur de connexion');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Recharger intelligemment les clients
   */
  const reloadCustomers = async () => {
    try {
      const response = await customerService.getCustomers();

      if (response.success) {
        const formattedCustomers = response.data.map(customer => 
          customerService.formatCustomerForDisplay(customer)
        );
        setCustomers(formattedCustomers);
        calculateStats(formattedCustomers);
        console.log('🔄 Liste des clients rechargée:', formattedCustomers.length);
      }
    } catch (error) {
      console.error('❌ Erreur lors du rechargement:', error);
    }
  };

  /**
   * Afficher les détails d'un client dans le modal
   */
  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailModal(true);
  };

  /**
   * Modifier directement depuis la vue détaillée
   */
  const handleEditFromDetail = (customer) => {
    setShowDetailModal(false); // Fermer le modal de détails
    setTimeout(() => {
      handleOpenEditModal(customer); // Ouvrir le modal de modification
    }, 300); // Délai pour l'animation
  };

  /**
   * Recherche en temps réel
   */
  const handleSearch = async (query) => {
    setSearchTerm(query);
    
    if (query.length >= 2) {
      try {
        const response = await customerService.searchCustomers(query);
        if (response.success) {
          const searchResults = response.data.map(customer => 
            customerService.formatCustomerForDisplay(customer)
          );
          setFilteredCustomers(searchResults);
        }
      } catch (error) {
        console.error('❌ Erreur de recherche:', error);
      }
    }
  };

  /**
   * Formater le montant des achats
   */
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Chargement des clients...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* En-tête avec statistiques */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">
              <Users className="me-2" size={24} />
              Gestion des Clients
            </h2>
            <Button 
              variant="primary" 
              onClick={handleOpenCreateModal}
              size={isMobile ? "sm" : "md"}
            >
              <UserPlus size={16} className="me-1" />
              {isMobile ? 'Nouveau' : 'Nouveau Client'}
            </Button>
          </div>

          {/* Statistiques */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card className="stat-card h-100">
                <Card.Body className="text-center">
                  <Users size={24} className="text-primary mb-2" />
                  <h4 className="mb-0">{stats.total}</h4>
                  <small className="text-muted">Total Clients</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="stat-card h-100">
                <Card.Body className="text-center">
                  <TrendingUp size={24} className="text-warning mb-2" />
                  <h4 className="mb-0">{stats.vip}</h4>
                  <small className="text-muted">Clients VIP</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="stat-card h-100 border-success">
                <Card.Body className="text-center">
                  <div className="text-success mb-2">🏷️</div>
                  <h4 className="mb-0 text-success">{stats.resellers || 0}</h4>
                  <small className="text-muted">Revendeurs</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="stat-card h-100">
                <Card.Body className="text-center">
                  <CreditCard size={24} className="text-info mb-2" />
                  <h4 className="mb-0">{stats.withCredit}</h4>
                  <small className="text-muted">Avec Achats</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Barre de recherche et filtres */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Row className="g-3">
                {/* Recherche */}
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher par nom ou téléphone..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    {searchTerm && (
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                {/* Filtres */}
                <Col md={3}>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="all">Tous les clients</option>
                    <option value="reseller">🏷️ Revendeurs</option>
                    <option value="vip">Clients VIP</option>
                    <option value="regular">Clients réguliers</option>
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Select
                    value={filters.creditStatus}
                    onChange={(e) => setFilters({...filters, creditStatus: e.target.value})}
                  >
                    <option value="all">Tous</option>
                    <option value="has_credit">Avec achats</option>
                    <option value="no_credit">Sans achats</option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Mode d'affichage (desktop uniquement) */}
              {!isMobile && (
                <Row className="mt-3">
                  <Col>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {filteredCustomers.length} client(s) affiché(s)
                      </small>
                      <div>
                        <Button
                          variant={viewMode === 'cards' ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setViewMode('cards')}
                          className="me-1"
                        >
                          Cartes
                        </Button>
                        <Button
                          variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setViewMode('table')}
                        >
                          Tableau
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Messages d'erreur */}
      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Liste des clients */}
      <Row>
        <Col>
          {filteredCustomers.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <Users size={48} className="text-muted mb-3" />
                <h5>Aucun client trouvé</h5>
                <p className="text-muted">
                  {searchTerm ? 
                    'Aucun client ne correspond à votre recherche.' :
                    'Commencez par créer votre premier client.'
                  }
                </p>
                <Button 
                  variant="primary" 
                  onClick={handleOpenCreateModal}
                >
                  <UserPlus size={16} className="me-1" />
                  Créer un client
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <>
              {/* Affichage en cartes (mobile et desktop) */}
              {viewMode === 'cards' && (
                <Row className="g-3">
                  {filteredCustomers.map(customer => (
                    <Col key={customer.id} xs={12} md={6} lg={4}>
                      <Card className="customer-card h-100">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0 customer-name">{customer.name}</h6>
                            <div className="d-flex gap-1 flex-wrap">
                              {customer.isReseller && (
                                <Badge bg="success" title={`Remise: ${customer.resellerDiscount} FCFA/produit`}>
                                  🏷️ Revendeur
                                </Badge>
                              )}
                              {customer.isVip && (
                                <Badge bg="warning" text="dark">VIP</Badge>
                              )}
                              {customer.totalPurchases > 0 && (
                                <Badge bg="info">Client</Badge>
                              )}
                            </div>
                          </div>

                          {/* Afficher la remise revendeur */}
                          {customer.isReseller && (
                            <div className="mb-2 p-2 bg-success bg-opacity-10 rounded small">
                              <strong>Remise revendeur:</strong> -{customer.resellerDiscount} FCFA/produit
                            </div>
                          )}

                          <div className="customer-details mb-3">
                            {customer.phone && (
                              <div className="contact-item">
                                <Phone size={14} className="me-1" />
                                <small>{customer.phone}</small>
                              </div>
                            )}
                            {customer.email && (
                              <div className="contact-item">
                                <Mail size={14} className="me-1" />
                                <small>{customer.email}</small>
                              </div>
                            )}
                            {customer.address && (
                              <div className="contact-item">
                                <MapPin size={14} className="me-1" />
                                <small>{customer.address}</small>
                              </div>
                            )}
                          </div>

                          <div className="customer-stats mb-3">
                            <Row className="g-2 text-center">
                              <Col xs={6}>
                                <div className="stat-mini">
                                  <strong>{customer.purchaseCount || 0}</strong>
                                  <br />
                                  <small className="text-muted">Achats</small>
                                </div>
                              </Col>
                              <Col xs={6}>
                                <div className="stat-mini">
                                  <strong>{formatAmount(customer.totalPurchases)}</strong>
                                  <br />
                                  <small className="text-muted">Total</small>
                                </div>
                              </Col>
                            </Row>
                          </div>

                          <div className="d-grid">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewCustomer(customer)}
                            >
                              <Eye size={14} className="me-1" />
                              Voir détails
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Affichage en tableau (desktop uniquement) */}
              {viewMode === 'table' && !isMobile && (
                <Card>
                  <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Client</th>
                          <th>Contact</th>
                          <th>Statut</th>
                          <th>Achats</th>
                          <th>Total</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCustomers.map(customer => (
                          <tr key={customer.id}>
                            <td>
                              <div>
                                <strong>{customer.name}</strong>
                                {customer.address && (
                                  <div className="text-muted small">
                                    <MapPin size={12} className="me-1" />
                                    {customer.address}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                {customer.phone && (
                                  <div className="contact-item">
                                    <Phone size={12} className="me-1" />
                                    <small>{customer.phone}</small>
                                  </div>
                                )}
                                {customer.email && (
                                  <div className="contact-item">
                                    <Mail size={12} className="me-1" />
                                    <small>{customer.email}</small>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                {customer.isReseller && (
                                  <Badge bg="success" title={`Remise: ${customer.resellerDiscount} FCFA`}>
                                    🏷️ Revendeur
                                  </Badge>
                                )}
                                {customer.isVip && (
                                  <Badge bg="warning" text="dark">VIP</Badge>
                                )}
                                {customer.totalPurchases > 0 ? (
                                  <Badge bg="info">Client</Badge>
                                ) : (
                                  <Badge bg="secondary">Nouveau</Badge>
                                )}
                              </div>
                            </td>
                            <td>
                              <strong>{customer.purchaseCount || 0}</strong>
                            </td>
                            <td>
                              <strong className="text-primary">
                                {formatAmount(customer.totalPurchases)}
                              </strong>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleViewCustomer(customer)}
                                  title="Voir détails"
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleOpenEditModal(customer)}
                                  title="Modifier"
                                >
                                  <Edit size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Modal de création/modification de client */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? (
              <>
                <Edit size={20} className="me-2" />
                Modifier le Client
              </>
            ) : (
              <>
                <UserPlus size={20} className="me-2" />
                Nouveau Client
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={customerForm.name}
                    onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})}
                    placeholder="Nom complet du client"
                    required
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Téléphone</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Phone size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="tel"
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                      placeholder="+221 XX XXX XX XX"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Mail size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      value={customerForm.email}
                      onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                      placeholder="email@exemple.com"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Adresse</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={customerForm.address}
                    onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})}
                    placeholder="Adresse complète (optionnel)"
                  />
                </Form.Group>
              </Col>
              
              {/* Section Revendeur */}
              <Col xs={12}>
                <Card className="bg-light border-0 mt-2">
                  <Card.Body className="py-3">
                    <Form.Check
                      type="switch"
                      id="isReseller"
                      label={
                        <span className="fw-bold">
                          🏷️ Client revendeur
                        </span>
                      }
                      checked={customerForm.isReseller}
                      onChange={(e) => setCustomerForm({
                        ...customerForm, 
                        isReseller: e.target.checked
                      })}
                      className="mb-2"
                    />
                    
                    {customerForm.isReseller && (
                      <Form.Group className="mt-2">
                        <Form.Label className="small text-muted">
                          Remise par produit (FCFA)
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>-</InputGroup.Text>
                          <Form.Control
                            type="number"
                            value={customerForm.resellerDiscount}
                            onChange={(e) => setCustomerForm({
                              ...customerForm, 
                              resellerDiscount: parseInt(e.target.value) || 3000
                            })}
                            min="0"
                            step="500"
                          />
                          <InputGroup.Text>FCFA</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Cette remise sera appliquée automatiquement sur chaque produit lors des ventes.
                        </Form.Text>
                      </Form.Group>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowCreateModal(false)}
            disabled={processing}
          >
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveCustomer}
            disabled={processing || !customerForm.name.trim()}
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-1" />
                {isEditing ? 'Modification...' : 'Création...'}
              </>
            ) : (
              <>
                <Check size={16} className="me-1" />
                {isEditing ? 'Modifier le client' : 'Créer le client'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de détails client avec modification intégrée */}
      {selectedCustomer && (
        <Modal 
          show={showDetailModal} 
          onHide={() => setShowDetailModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Users size={20} className="me-2" />
              Détails du Client
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-4">
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Informations personnelles</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="client-info">
                      <div className="info-item">
                        <strong>Nom:</strong> {selectedCustomer.name}
                      </div>
                      {selectedCustomer.phone && (
                        <div className="info-item">
                          <Phone size={14} className="me-1" />
                          <strong>Téléphone:</strong> {selectedCustomer.phone}
                        </div>
                      )}
                      {selectedCustomer.email && (
                        <div className="info-item">
                          <Mail size={14} className="me-1" />
                          <strong>Email:</strong> {selectedCustomer.email}
                        </div>
                      )}
                      {selectedCustomer.address && (
                        <div className="info-item">
                          <MapPin size={14} className="me-1" />
                          <strong>Adresse:</strong> {selectedCustomer.address}
                        </div>
                      )}
                    </div>
                    
                    {/* Section Revendeur */}
                    {selectedCustomer.isReseller && (
                      <div className="mt-3 p-3 bg-success bg-opacity-10 rounded">
                        <div className="d-flex align-items-center mb-2">
                          <Badge bg="success" className="me-2">🏷️ Revendeur</Badge>
                        </div>
                        <div className="small">
                          <strong>Remise par produit:</strong>{' '}
                          <span className="text-success fw-bold">
                            -{selectedCustomer.resellerDiscount} FCFA
                          </span>
                        </div>
                        <div className="text-muted small mt-1">
                          Cette remise est appliquée automatiquement sur chaque produit lors des ventes.
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Statistiques d'achat</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="stats-grid">
                      <div className="stat-item">
                        <div className="stat-value">{selectedCustomer.purchaseCount || 0}</div>
                        <div className="stat-label">Nombre d'achats</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value text-primary">
                          {formatAmount(selectedCustomer.totalPurchases)}
                        </div>
                        <div className="stat-label">Total des achats</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">
                          <div className="d-flex gap-1 justify-content-center flex-wrap">
                            {selectedCustomer.isReseller && (
                              <Badge bg="success">Revendeur</Badge>
                            )}
                            {selectedCustomer.isVip ? (
                              <Badge bg="warning" text="dark">VIP</Badge>
                            ) : (
                              <Badge bg="secondary">Régulier</Badge>
                            )}
                          </div>
                        </div>
                        <div className="stat-label">Statut</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
            <Button 
              variant="primary" 
              onClick={() => handleEditFromDetail(selectedCustomer)}
            >
              <Edit size={16} className="me-1" />
              Modifier
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Styles CSS */}
      <style jsx>{`
        .stat-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          transition: transform 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
        }

        .customer-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.06);
          transition: transform 0.2s;
        }

        .customer-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
        }

        .customer-name {
          color: #2d3748;
          font-weight: 600;
        }

        .contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.25rem;
          color: #4a5568;
        }

        .customer-details {
          min-height: 60px;
        }

        .stat-mini {
          padding: 0.5rem;
          background: #f7fafc;
          border-radius: 6px;
        }

        .client-info .info-item {
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .client-info .info-item:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        .stat-item {
          text-align: center;
          padding: 1rem;
          background: #f7fafc;
          border-radius: 8px;
        }

        .stat-value {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #4a5568;
        }

        @media (max-width: 768px) {
          .customer-card .customer-details {
            min-height: auto;
          }
          
          .stat-mini {
            padding: 0.25rem;
            font-size: 0.875rem;
          }
        }
      `}</style>
    </Container>
  );
};

export default CustomersList;